import React, { useEffect, useRef, useState } from 'react';
import ChangePasswordModal from './ChangePasswordModal';

function Avatar({ username, size = 'md', online = false }) {
  const colors = ['bg-violet-600', 'bg-indigo-600', 'bg-sky-600', 'bg-teal-600', 'bg-emerald-600', 'bg-pink-600', 'bg-rose-600'];
  const color = username ? colors[username.charCodeAt(0) % colors.length] : 'bg-slate-600';
  const sz = size === 'sm' ? 'h-9 w-9 text-xs' : size === 'lg' ? 'h-12 w-12 text-base' : 'h-10 w-10 text-sm';
  return (
    <div className="relative flex-shrink-0">
      <div className={`flex items-center justify-center rounded-full font-semibold text-white ${sz} ${color}`}>
        {username ? username.slice(0, 2).toUpperCase() : '??'}
      </div>
      {online && (
        <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-[#111827] bg-emerald-400" />
      )}
    </div>
  );
}

export default function FriendsList({ token, apiUrl, selectedFriend, onSelect, presence, user, onLogout, onAdmin, onMedia, onGoHome }) {
  const [friends, setFriends] = useState([]);
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [requests, setRequests] = useState([]);
  const [tab, setTab] = useState('chats'); // 'chats' | 'search'
  const [sentIds, setSentIds] = useState(new Set());
  const [menuOpen, setMenuOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  const bearer = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchFriends();
    fetchRequests();
  }, []);

  const fetchFriends = async () => {
    const res = await fetch(`${apiUrl}/friends`, { headers: bearer });
    setFriends(await res.json());
  };

  const fetchRequests = async () => {
    const res = await fetch(`${apiUrl}/friends/requests`, { headers: bearer });
    const data = await res.json();
    setRequests(data);
  };

  const searchUsers = async (value) => {
    setSearchQuery(value);
    if (!value.trim()) { setResults([]); return; }
    const res = await fetch(`${apiUrl}/friends/search?q=${encodeURIComponent(value)}`, { headers: bearer });
    setResults(await res.json());
  };

  const sendRequest = async (toUserId) => {
    await fetch(`${apiUrl}/friends/requests`, {
      method: 'POST',
      headers: { ...bearer, 'Content-Type': 'application/json' },
      body: JSON.stringify({ toUserId }),
    });
    setSentIds((prev) => new Set([...prev, toUserId]));
  };

  const respondRequest = async (id, action) => {
    await fetch(`${apiUrl}/friends/requests/${id}/respond`, {
      method: 'POST',
      headers: { ...bearer, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action }),
    });
    fetchFriends();
    fetchRequests();
  };

  const onlineIds = new Set(presence.map((p) => p.id));

  const edgeSwipeStart = useRef(null);

  return (
    <>
      {pwOpen && <ChangePasswordModal apiUrl={apiUrl} token={token} onClose={() => setPwOpen(false)} />}

      <div
        className="flex h-full flex-col bg-[#111827]"
        onTouchStart={(e) => {
          const t = e.touches[0];
          edgeSwipeStart.current = t.clientX < 40 ? { x: t.clientX, y: t.clientY } : null;
        }}
        onTouchEnd={(e) => {
          if (!edgeSwipeStart.current || !onGoHome) return;
          const dx = e.changedTouches[0].clientX - edgeSwipeStart.current.x;
          const dy = e.changedTouches[0].clientY - edgeSwipeStart.current.y;
          if (dx > 70 && Math.abs(dx) > Math.abs(dy) * 1.2) onGoHome();
          edgeSwipeStart.current = null;
        }}
      >
        {/* Header — padded for iOS status bar */}
        <div className="flex items-center justify-between px-4 border-b border-white/5"
          style={{ paddingTop: 'calc(env(safe-area-inset-top) + 12px)', paddingBottom: '12px' }}>
          <div className="flex items-center gap-2.5">
            <Avatar username={user?.username} size="sm" online />
            <div>
              <p className="text-sm font-semibold text-white leading-tight">{user?.username}</p>
              <p className="text-xs text-emerald-400 leading-tight">Active now</p>
            </div>
          </div>

          <div className="relative">
            <button
              onClick={() => setMenuOpen((o) => !o)}
              className="rounded-full p-2 text-slate-400 hover:bg-white/10 hover:text-white transition"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
              </svg>
            </button>

            {menuOpen && (
              <div
                className="absolute right-0 top-9 z-50 w-48 rounded-xl border border-white/10 bg-[#1e2433] shadow-xl py-1"
                onMouseLeave={() => setMenuOpen(false)}
              >
                {onAdmin && (
                  <button
                    onClick={() => { onAdmin(); setMenuOpen(false); }}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-white/10 hover:text-white"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 011.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.559.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.894.149c-.424.07-.764.383-.929.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 01-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.398.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 01-.12-1.45l.527-.737c.25-.35.272-.806.108-1.204-.165-.397-.506-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.108-1.204l-.526-.738a1.125 1.125 0 01.12-1.45l.773-.773a1.125 1.125 0 011.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Admin Panel
                  </button>
                )}
                {onGoHome && (
                  <button
                    onClick={() => { onGoHome(); setMenuOpen(false); }}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-white/10 hover:text-white"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75" />
                    </svg>
                    Home
                  </button>
                )}
                {onMedia && (
                  <button
                    onClick={() => { onMedia(); setMenuOpen(false); }}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-white/10 hover:text-white"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                    </svg>
                    Media
                  </button>
                )}
                <button
                  onClick={() => { setPwOpen(true); setMenuOpen(false); }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-white/10 hover:text-white"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                  </svg>
                  Change Password
                </button>
                <button
                  onClick={async () => {
                    setMenuOpen(false);
                    try {
                      if ('serviceWorker' in navigator) {
                        const regs = await navigator.serviceWorker.getRegistrations();
                        await Promise.all(regs.map((r) => r.update()));
                      }
                      const keys = await caches.keys();
                      await Promise.all(keys.map((k) => caches.delete(k)));
                    } finally {
                      window.location.reload();
                    }
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-white/10 hover:text-white"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                  </svg>
                  Refresh App
                </button>
                <div className="my-1 border-t border-white/10" />
                <button
                  onClick={() => { onLogout(); setMenuOpen(false); }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                  </svg>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Search bar */}
        <div className="px-3 py-2.5 border-b border-white/5">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 15.803a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              value={searchQuery}
              onChange={(e) => { searchUsers(e.target.value); setTab(e.target.value ? 'search' : 'chats'); }}
              onFocus={() => { if (searchQuery) setTab('search'); }}
              placeholder="Search people…"
              className="w-full rounded-full bg-white/8 border border-white/10 pl-9 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:border-indigo-500/50 focus:outline-none focus:bg-white/10 transition"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {tab === 'search' ? (
            <div className="px-2 py-2">
              <p className="px-2 pb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">People</p>
              {results.length === 0 && searchQuery && (
                <p className="px-3 py-3 text-sm text-slate-500 text-center">No users found</p>
              )}
              {results.map((u) => (
                <div key={u.id} className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-white/5">
                  <Avatar username={u.username} size="sm" online={onlineIds.has(u.id)} />
                  <span className="flex-1 text-sm font-medium text-slate-200 truncate">{u.username}</span>
                  <button
                    onClick={() => sendRequest(u.id)}
                    disabled={sentIds.has(u.id)}
                    className="rounded-full bg-indigo-600 px-3 py-1 text-xs font-semibold text-white hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-default transition"
                  >
                    {sentIds.has(u.id) ? 'Sent' : 'Add'}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Friend Requests */}
              {requests.length > 0 && (
                <div className="px-2 py-2 border-b border-white/5">
                  <p className="px-2 pb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Requests · {requests.length}
                  </p>
                  {requests.map((req) => (
                    <div key={req.id} className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-white/5">
                      <Avatar username={req.from_username} size="sm" />
                      <span className="flex-1 text-sm font-medium text-slate-200 truncate">{req.from_username}</span>
                      <button
                        onClick={() => respondRequest(req.id, 'accept')}
                        className="rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-500 transition"
                      >
                        Accept
                      </button>
                      <button
                        onClick={() => respondRequest(req.id, 'decline')}
                        className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-slate-300 hover:bg-white/20 transition"
                      >
                        Decline
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Chats */}
              <div className="px-2 py-2">
                {friends.length === 0 && (
                  <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                    <svg className="h-10 w-10 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
                    </svg>
                    <p className="text-sm text-slate-500">No chats yet</p>
                    <p className="text-xs text-slate-600">Search to add friends</p>
                  </div>
                )}
                {friends.map((friend) => {
                  const isOnline = onlineIds.has(friend.id);
                  const isSelected = selectedFriend?.id === friend.id;
                  return (
                    <button
                      key={friend.id}
                      onClick={() => onSelect(friend)}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition
                        ${isSelected ? 'bg-indigo-600/20 ring-1 ring-indigo-500/30' : 'hover:bg-white/5'}`}
                    >
                      <Avatar username={friend.username} online={isOnline} />
                      <div className="min-w-0 flex-1">
                        <p className={`truncate text-sm font-medium ${isSelected ? 'text-white' : 'text-slate-200'}`}>
                          {friend.username}
                        </p>
                        <p className={`text-xs ${isOnline ? 'text-emerald-400' : 'text-slate-500'}`}>
                          {isOnline ? 'Active now' : 'Offline'}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* App version */}
        <div className="flex-shrink-0 border-t border-white/5 px-4 py-2 text-center">
          <p className="text-[10px] text-slate-600">{process.env.REACT_APP_VERSION || 'dev'}</p>
        </div>
      </div>
    </>
  );
}
